<template>
  <!-- Staff shell: full role-adaptive tabbed workspace. Staff drives the
       explicit condition; the customer shell is the fail-closed fallback. -->
  <template v-if="isStaffUser">
    <PageHeader
      title="Support"
      subtitle="Manage requirements and support tickets"
    >
      <template #bottom>
        <Page_Tabs
          :tabs="tabOptions"
          class="px-6 pt-4"
          no-border
        />
      </template>
    </PageHeader>

    <div :class="isFullBleed ? '' : 'p-6'">
      <div :class="isFullBleed ? '' : 'bg-white rounded border border-gray-200'">
        <Support_ServiceDesk v-if="activeTab === 'service-desk'" :selected-id="selectedId" @select="onSelect" />
        <Support_Activity v-else-if="activeTab === 'activity'" />
        <Support_Hades v-else-if="activeTab === 'hades'" />
        <Support_List
          v-else
          :selected-id="selectedId"
          @select="onSelect"
        />
      </div>
    </div>
  </template>

  <!-- Customer shell: bare Service Desk, no internal chrome (absorbs the retired
       Support_Standalone). Fail-closed default for any non-staff user. -->
  <div v-else class="h-screen flex flex-col overflow-hidden">
    <Support_ServiceDesk :selected-id="selectedId" @select="onSelect" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Support_List from './Support_List.vue';
import Support_ServiceDesk from './Support_ServiceDesk.vue';
import Support_Activity from './Support_Activity.vue';
import Support_Hades from './Hades/Support_Hades.vue';
import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue';
import Page_Tabs from '@/components/widgetcomponents/Page_Tabs.vue';

// Requirements + Tickets merged into Work; Timeline is gone -- it was
// /api/requirements/ filtered to DONE_STATUSES ordered by -completed_at, which
// the merged list reproduces as Status = Done sorted by completed date. Activity
// survives because it reads /api/requirements/activity/, which returns events.
const TABS = [
  { label: 'Work', icon: ['fas', 'clipboard-list'], to: '/support/work' },
  { label: 'Activity', icon: ['fas', 'wave-square'], to: '/support/activity' },
  { label: 'Hades', icon: ['fas', 'magnifying-glass-chart'], to: '/support/hades' },
  { label: 'Service Desk', icon: ['fas', 'headset'], to: '/support/service-desk' },
];

export default {
  name: 'Support_Overview',
  components: {
    Support_List,
    Support_ServiceDesk,
    Support_Activity,
    Support_Hades,
    PageHeader,
    Page_Tabs,
  },
  props: {
    selectedId: { type: [String, Number], default: null },
  },
  computed: {
    ...mapGetters('auth', ['current_user']),
    isStaffUser() {
      // Kairon staff. Same predicate as Support_ServiceDesk / Support_ItemDetail
      // so the staff/customer split is consistent everywhere.
      return !!(this.current_user?.is_staff || this.current_user?.is_superuser);
    },
    isCustomerShell() {
      return !this.isStaffUser;
    },
    tabOptions() {
      return TABS;
    },
    isFullBleed() {
      // Service Desk manages its own header/toolbar; other tabs get card chrome.
      return this.activeTab === 'service-desk';
    },
    activeTab() {
      const path = this.$route.path;
      if (path.includes('service-desk')) return 'service-desk';
      if (path.includes('activity')) return 'activity';
      if (path.includes('hades')) return 'hades';
      return 'work';
    },
  },
  watch: {
    $route: 'ensureCustomerRoute',
  },
  created() {
    this.ensureCustomerRoute();
  },
  methods: {
    ensureCustomerRoute() {
      // A customer may hold MODULE_SERVICE_DESK (so the route guard admits them
      // to any /support/* path) but must only ever see the Service Desk. Keep
      // them there.
      if (this.isCustomerShell && this.activeTab !== 'service-desk') {
        this.$router.replace({ path: '/support/service-desk', query: this.$route.query });
      }
    },
    onSelect(id) {
      const bases = {
        'service-desk': '/support/service-desk',
        work: '/support/work',
      };
      const base = bases[this.activeTab] || bases.work;
      this.$router.push({
        path: id ? `${base}/${id}` : base,
        query: this.$route.query,
      });
    },
  },
};
</script>
